// Pure logic for the canvas's destructive persistence path. Deleting a node
// must also delete its connected edges (the nodes lambda doesn't cascade), so
// the set of deletes is computed here — pure and unit-tested — and executed
// against an injected API slice so tests can assert exactly which deletes are
// issued and that nothing else is touched.

import type { ApiResponse } from '@/types/api';

/** The slice of the API client the deletion executor needs. */
export interface CanvasDeleteApi {
  deleteNode(id: string, projectId: string): Promise<ApiResponse<unknown>>;
  deleteEdge(id: string, projectId: string): Promise<ApiResponse<unknown>>;
}

/** Ids of the edges that touch a node (either endpoint) and must go with it. */
export function connectedEdgeIds(
  nodeId: string,
  edges: Array<{ id: string; source: string; target: string }>,
): string[] {
  return edges.filter((e) => e.source === nodeId || e.target === nodeId).map((e) => e.id);
}

async function expectSuccess(op: Promise<ApiResponse<unknown>>): Promise<void> {
  const res = await op;
  if (!res.success) throw new Error(res.message || 'Request failed');
}

async function attemptAll(ops: Promise<void>[]): Promise<void> {
  const results = await Promise.allSettled(ops);
  const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
  if (failures.length > 0) {
    const first = failures[0].reason;
    throw new Error(first instanceof Error ? first.message : 'Delete failed');
  }
}

/**
 * Issue the given deletes (and only those): all edges first, then nodes only
 * if every edge delete succeeded. The nodes lambda doesn't cascade, so a node
 * delete that lands while an edge delete fails would strand orphan edges in
 * the DB; the reverse failure just leaves the node in place for the rollback
 * refetch to restore. Within each wave every delete is attempted even if some
 * fail, then a single aggregate error is thrown so the caller rolls back once.
 */
export async function persistGraphDeletions(
  api: CanvasDeleteApi,
  projectId: string,
  nodeIds: string[],
  edgeIds: string[],
): Promise<void> {
  await attemptAll(edgeIds.map((id) => expectSuccess(api.deleteEdge(id, projectId))));
  await attemptAll(nodeIds.map((id) => expectSuccess(api.deleteNode(id, projectId))));
}
